import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { patchJSON } from '@/lib/api'
import { accountingErrorMessage } from '@/pages/accountingErrors'
import {
  AccountingMappingSelect,
  type AccountingProviderReference,
} from '@/pages/AccountingMappingSelect'

export interface PayoutAccountMappingsResponse {
  payout_account_mappings: {
    bank_accounts: AccountingProviderReference[]
    expense_accounts: AccountingProviderReference[]
    accounts_read_only: boolean
    mappings: Array<{
      mapping_kind: 'bank' | 'processor_fee' | 'platform_fee'
      account: AccountingProviderReference | null
    }>
  }
}

const labels = {
  bank: 'Bank account',
  processor_fee: 'Processor fees',
  platform_fee: 'Platform Fee',
}

export function PayoutAccountMappings({
  query,
  readOnly,
}: {
  query: ReturnType<typeof useQuery<PayoutAccountMappingsResponse>>
  readOnly: boolean
}) {
  const queryClient = useQueryClient()
  const update = useMutation({
    mutationFn: ({ mappingKind, accountId }: { mappingKind: string; accountId: string | null }) =>
      patchJSON<PayoutAccountMappingsResponse>('/api/v1/payout_account_mappings', {
        payout_account_mappings: { mapping_kind: mappingKind, account_id: accountId },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['payout-account-mappings'], data)
      queryClient.invalidateQueries({ queryKey: ['payouts'] })
      queryClient.invalidateQueries({ queryKey: ['accounting-sync-works'] })
    },
  })

  if (query.isLoading)
    return <p className="text-body-sm text-muted-foreground">Loading Payout accounts...</p>
  if (query.isError || !query.data) return <Alert>Payout accounts unavailable</Alert>

  const payload = query.data.payout_account_mappings
  return (
    <section className="p-4">
      <h2 className="text-body font-semibold">Payout deposit accounts</h2>
      <div className="pt-3">
        {update.error && <Alert className="mb-3">{accountingErrorMessage(update.error)}</Alert>}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payout use</TableHead>
              <TableHead>Chart-of-accounts choice</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payload.mappings.map((mapping) => (
              <TableRow key={mapping.mapping_kind}>
                <TableCell className="font-medium">{labels[mapping.mapping_kind]}</TableCell>
                <TableCell>
                  {readOnly ? (
                    <span className="text-body-sm text-muted-foreground">
                      {mapping.account?.name ?? 'Unmapped'}
                    </span>
                  ) : (
                    <AccountingMappingSelect
                      destinations={
                        mapping.mapping_kind === 'bank'
                          ? payload.bank_accounts
                          : payload.expense_accounts
                      }
                      value={mapping.account?.id}
                      disabled={update.isPending}
                      onChange={(accountId) =>
                        update.mutate({ mappingKind: mapping.mapping_kind, accountId })
                      }
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}
